import * as React from 'react';
import * as PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Field, Form, formValueSelector, reduxForm } from 'redux-form/immutable';
import { createStructuredSelector } from 'reselect';
import { constant, get, includes, isEqual, noop } from 'lodash';
import classnames from 'classnames';
import config from 'config';
import Button from 'components/Button/index';
import renderCheckbox from 'components/Checkbox/reduxForm';
import renderField from 'components/Field/reduxForm';

import ChangePasswordSection from './ChangePasswordSection';
import CreatePasswordSection from './CreatePasswordSection';
import { sendVerificationEmail } from '../../../services/API';
import errorMessages from 'constants/errorMessages';
import { ReactComponent as WarningIcon } from '../../../../../images/red-30.svg';
import styles from './ContactInfoForm.module.scss';
import { validateMinLength } from 'utils/reduxForm/validations';
import { remotePaths } from 'router/routes';

const formName = 'contactInfo';
const minLength = validateMinLength(3, 'Контрольное слово должно быть не менее 3 символов');

class ContactInfoForm extends React.Component {
  static propTypes = {
    isReadOnly: PropTypes.bool.isRequired,
    isInspectorArm: PropTypes.bool.isRequired,

    // provide by connect and redux form
    email: PropTypes.string,
    password: PropTypes.string,
    confirmPassword: PropTypes.string,
    oldPassword: PropTypes.string,
    allowRestoring: PropTypes.bool,

    hasCodeWord: PropTypes.bool.isRequired,
    isEmptyPassword: PropTypes.bool.isRequired,
    isEmailConfirm: PropTypes.bool.isRequired,
    emailIsChanged: PropTypes.bool.isRequired,

    autofill: PropTypes.func.isRequired,
    dispatch: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    submitting: PropTypes.bool.isRequired,
    submitSucceeded: PropTypes.bool.isRequired,
    dirty: PropTypes.bool.isRequired,

    innerError: PropTypes.bool,
    isShowPassword: PropTypes.bool.isRequired,
    isSavedSettings: PropTypes.bool.isRequired,
    toggleChangePassword: PropTypes.func,
    setInnerError: PropTypes.func,
    updateContactData: PropTypes.func,
    submit: PropTypes.func,
    invalid: PropTypes.bool,
    initialized: PropTypes.bool,
    formErrors: PropTypes.object,
  };

  state = {
    sendEmail: false,
    showCodeWordForm: false,
  };

  componentDidUpdate() {
    const { autofill, emailIsChanged } = this.props;

    if (emailIsChanged) {
      autofill('emailSubscribe', false);
      autofill('allowRestoring', false);
    }
  }

  getEmailMessage() {
    const { email, isEmailConfirm, emailIsChanged } = this.props;
    const { sendEmail } = this.state;

    if (sendEmail && !emailIsChanged) {
      return {
        isValid: true,
        validationMessage: 'Отправлено письмо для подтверждения',
      };
    }

    if (email && !isEmailConfirm && !emailIsChanged) {
      return {
        isValid: false,
        validationMessage: 'Адрес электронной почты не подтвержден',
      };
    }
  }

  handleValidateEmail = () => {
    const { sendEmail } = this.state;

    if (sendEmail) {
      return;
    }

    sendVerificationEmail().then((data) => {
      if (data.isOk) {
        this.setState(
          {
            sendEmail: true,
          },
          () => {
            this.props.updateContactData();
          },
        );
      } else {
        this.props.setInnerError(errorMessages.defaultServerError);
      }
    });
  };
  toggleChangePasswordSection = (event) => {
    event.preventDefault();

    this.props.toggleChangePassword();
  };

  toggleCodeWordForm = () => {
    this.setState({ showCodeWordForm: !this.state.showCodeWordForm });
  };

  handleSubmitClick = (event) => {
    event.preventDefault();
    if (!this.props.invalid) {
      this.setState(
        {
          showCodeWordForm: false,
          sendEmail: false,
        },
        () => {
          this.props.submit();
        },
      );
    } else {
      this.props.submit();
    }
  };

  validateEmail(email) {
    if (!email) {
      return false;
    }

    return /^[a-zа-яё0-9._%+-]+@[a-zа-яё0-9.-]+\.[a-zа-яё]{2,6}$/i.test(email);
  }

  renderRestoreSection() {
    const {
      hasCodeWord,
      allowRestoring,
      isEmailConfirm,
      emailIsChanged,
      isReadOnly,
      isInspectorArm,
    } = this.props;
    const { showCodeWordForm } = this.state;
    const isDisabled =
      isReadOnly || isInspectorArm || this.state.sendEmail || !isEmailConfirm || emailIsChanged;

    const allowRestoringCheckbox = (
      <div className="form_item">
        <Field
          isDisabled={isDisabled}
          name="allowRestoring"
          component={renderCheckbox}
          label="Разрешить восстановление пароля по адресу электронной почты"
        />
      </div>
    );

    if (!allowRestoring) {
      return allowRestoringCheckbox;
    }

    return (
      <div>
        {allowRestoringCheckbox}
        {hasCodeWord ? (
          <div className="form_item js-change-word-wrap">
            {'Кодовое слово задано'}{' '}
            {isDisabled ? null : <a onClick={this.toggleCodeWordForm}>{'изменить'}</a>}
          </div>
        ) : (
          <div className="form_item js-change-word-wrap">
            {'Кодовое слово не задано'}{' '}
            {isDisabled ? null : <a onClick={this.toggleCodeWordForm}>{'задать'}</a>}
          </div>
        )}

        {showCodeWordForm ? (
          <div className="cRow">
            <div className="cCol cCol__50 form_item">
              <Field
                name="codeWord"
                validate={minLength}
                component={renderField}
                label="Кодовое слово"
                placeholder="Кодовое слово"
              />
            </div>
            <div className="cCol cCol__50 form_item">
              <div className="form_note-beside">
                <div className="form_table">
                  <div className="form_cell">
                    {'Потребуется при восстановлении пароля по электронной почте'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  render() {
    const {
      isReadOnly,
      isInspectorArm,
      isEmptyPassword,
      isEmailConfirm,
      emailIsChanged,
      initialized,
      submitting,
      dirty,
      handleSubmit,
      formErrors,
      innerError,
      email,
      password,
      isSavedSettings,
      isShowPassword,
      submitSucceeded,
    } = this.props;

    const { sendEmail } = this.state;

    const isDisabled = isReadOnly || isInspectorArm;
    const emailLinkIsDisabled = isDisabled || !this.validateEmail(email) || emailIsChanged;

    if (!initialized) {
      return null;
    }

    return (
      <Form className="form" onSubmit={handleSubmit}>
        <Field component="input" type="hidden" name="sendEmail" />

        <div className="profile_top white-block_border white-block_border__bottom">
          {isReadOnly && (
            <div className="cRow" style={{ marginBottom: '30px' }}>
              <div className={styles.techWorkMessage}>
                <div className="cCol">
                  <WarningIcon />
                </div>
                <div className="cCol cCol__90">
                  {
                    'С 13 сентября (22.00 МСК) по 17 сентября (23.59 МСК) ведутся технические работы по улучшению Личного кабинета налогоплательщика для физических лиц. В связи с этим функционал изменения данных профиля и персональных данных в Личном кабинете будет недоступен. Просим учесть это при работе с Личным кабинетом. Мы заботимся о том, чтобы Вам было комфортно пользоваться онлайн сервисами ФНС России.'
                  }
                </div>
              </div>
            </div>
          )}
          <div className="cRow">
            <div className="cCol cCol__33 form-item profile_top__alone">
              <Field
                label="Телефон:"
                name="phone"
                component={renderField}
                placeholder="+7(999)999-99-99"
                mask="+7(111)111-11-11"
                isDisabled={isDisabled}
              />
            </div>

            <div className="cCol cCol__33 form-item">
              <Field
                name="email"
                label="Адрес электронной почты:"
                component={renderField}
                placeholder="email@nalog.ru"
                isDisabled={isDisabled}
                {...this.getEmailMessage()}
              />
            </div>

            <div className="cCol cCol__33 form_item">
              <div className="form_note-beside">
                <div className="form_table">
                  <div className="form_cell">
                    {isEmailConfirm && !emailIsChanged && !(formErrors || {}).email ? (
                      <div className="green">{'Адрес подтвержден'}</div>
                    ) : null}

                    {!isEmailConfirm && !emailIsChanged && this.validateEmail(email) ? (
                      <a
                        className={classnames('link link_mail', { grey: isDisabled || sendEmail })}
                        onClick={isDisabled ? noop : this.handleValidateEmail}
                      >
                        {'Отправить на данный адрес электронной почты письмо для его подтверждения'}
                      </a>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {!config.enableNotificationsCenter &&
          isEmailConfirm &&
          !this.state.sendEmail &&
          !(formErrors || {}).email ? (
            <div className="form_item">
              <Field
                name="emailSubscribe"
                component={renderCheckbox}
                label="Получать по электронной почте уведомления о событиях в Личном кабинете"
                isDisabled={emailLinkIsDisabled}
              />
            </div>
          ) : null}

          <div className="form_item">
            <a href={remotePaths.consentToInform} className="link_chat">
              Подать согласие (отказ) на информирование по СМС и (или) электронной почте о наличии
              недоимки и (или) задолженности
            </a>
          </div>

          <div>
            {isEmailConfirm && !(formErrors || {}).email ? this.renderRestoreSection() : null}
          </div>
          {!isReadOnly && (
            <div className="form_item">
              <a
                className="link link_change-pass"
                onClick={isDisabled ? noop : this.toggleChangePasswordSection}
              >
                {isEmptyPassword ? 'Задать пароль' : 'Сменить пароль'}
              </a>
            </div>
          )}
          {isShowPassword && isEmptyPassword ? <CreatePasswordSection password={password} /> : null}
          {isShowPassword && !isEmptyPassword ? (
            <ChangePasswordSection password={password} />
          ) : null}
          <div className="cRow profile_buttons">
            <div className="cCol cCol__33">
              <Button
                type={'submit'}
                isDisabled={submitSucceeded ? false : !dirty || isDisabled || submitting}
                onClick={this.handleSubmitClick}
              >
                {'Сохранить'}
              </Button>
            </div>

            {innerError ? (
              <div className="cCol cCol__66 js-change-message">
                <div className="form_table">
                  <div className="form_cell">
                    <div className="red">{innerError}</div>
                  </div>
                </div>
              </div>
            ) : null}

            {isSavedSettings ? (
              <div className="cCol cCol__33 js-change-message">
                <div className="form_table">
                  <div className="form_cell">
                    <div className="green">{'Изменения сохранены'}</div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </Form>
    );
  }
}

const makeMapStateToProps = (initState, ownProps) => {
  const select = formValueSelector(formName);

  const ALLOWED_AUTH_TYPES = ['ESIA', 'QES'];

  const getIntialValues = constant({
    sendEmail: false,
    phone: get(ownProps, 'contactInfo.phone'),
    email: get(ownProps, 'contactInfo.email'),
    allowRestoring: get(ownProps, 'contactInfo.allowRestoring'),
    emailSubscribe: get(ownProps, 'contactInfo.emailSubscribe'),
    oldPassword: '',
    newPassword1: '',
    newPassword2: '',
  });

  return createStructuredSelector({
    email: (state) => select(state, 'email'),
    oldPassword: (state) => select(state, 'oldPassword'),
    password: (state) => select(state, 'newPassword1'),
    confirmPassword: (state) => select(state, 'newPassword2'),
    allowRestoring: (state) => select(state, 'allowRestoring'),

    hasCodeWord: (state, props) => get(props, 'contactInfo.hasCodeWord'),
    isEmptyPassword: (state, props) => {
      const authType = get(props, 'contactInfo.loginMethod');
      return includes(ALLOWED_AUTH_TYPES, authType);
    },
    isEmailConfirm: (state, props) => get(props, 'contactInfo.isEmailConfirm'),
    emailIsChanged: (state, props) =>
      !isEqual(select(state, 'email'), get(props, 'contactInfo.email', '')),

    initialValues: getIntialValues,
  });
};

export default compose(
  connect(makeMapStateToProps),
  reduxForm({
    form: formName,
  }),
)(ContactInfoForm);
